import { useState } from "react";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatDateTimeWithOptionsBs } from "@/lib/format";
import { FileText, FilePdf, CircleNotch } from "@phosphor-icons/react";
import { openInvoiceReceipt } from "./invoice-receipt";
import type { CompanyCreditTransactionWithRefs, InvoiceData } from "@/types";

function fmtKm(n: number): string {
  return `${new Intl.NumberFormat("bs-BA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)} KM`;
}
function fmtDateTime(s: string): string {
  return formatDateTimeWithOptionsBs(s, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Sarajevo",
  });
}

const REASON_LABEL: Record<string, string> = {
  recharge: "Dopuna",
  session_deduction: "Naplata sesije",
  session_refund: "Povrat za otkazanu sesiju",
  manual_adjustment: "Ručna korekcija",
};
const REASON_COLOR: Record<string, string> = {
  recharge: "bg-emerald-500/10 text-emerald-700",
  session_deduction: "bg-amber-500/10 text-amber-700",
  session_refund: "bg-blue-500/10 text-blue-700",
  manual_adjustment: "bg-purple-500/10 text-purple-700",
};

export function CreditTransactionsTable({
  invoiceUrl,
  items,
  total,
  page,
  pageSize,
  onPageChange,
}: {
  // Dopuna sa fakturom vodi na FAK PDF (/api/company-invoices/:id/pdf). Stara
  // dopuna bez fakture otvara potvrdu o dopuni preko ovog URL-a; bez njega se
  // potvrda ne nudi (kolona ostaje zbog FAK linka).
  invoiceUrl?: (transactionId: string) => string;
  items: CompanyCreditTransactionWithRefs[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const showReceipt = !!invoiceUrl || items.some((tx) => !!tx.invoiceId);

  const generateReceipt = async (transactionId: string) => {
    if (!invoiceUrl) return;
    setLoadingId(transactionId);
    try {
      const res = await fetch(invoiceUrl(transactionId));
      await throwIfNotOk(res, "Greška pri generisanju potvrde");
      const invoice = (await res.json()) as InvoiceData;
      openInvoiceReceipt(invoice);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Greška pri generisanju potvrde");
    } finally {
      setLoadingId(null);
    }
  };
  return (
    <div className="rounded-xl border border-border/60 bg-card shadow-sm">
      <div className="border-b px-4 py-3">
        <p className="text-sm font-semibold text-foreground">Historija transakcija</p>
        <p className="text-xs text-muted-foreground">{total} stavki ukupno</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2">Datum</th>
              <th className="px-4 py-2">Tip</th>
              <th className="px-4 py-2 text-right">Iznos</th>
              <th className="px-4 py-2">Referenca</th>
              <th className="px-4 py-2">Napomena</th>
              {showReceipt && <th className="px-4 py-2 text-right">Dokument</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td className="px-4 py-8 text-center text-muted-foreground" colSpan={showReceipt ? 6 : 5}>
                  Nema transakcija
                </td>
              </tr>
            )}
            {items.map((tx) => (
              <tr key={tx.id} className="border-b">
                <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(tx.createdAt)}</td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "inline-flex rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                      REASON_COLOR[tx.reason] ?? "bg-muted text-foreground"
                    )}
                  >
                    {REASON_LABEL[tx.reason] ?? tx.reason}
                  </span>
                </td>
                <td
                  className={cn(
                    "whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums",
                    tx.amount > 0 ? "text-emerald-700" : tx.amount < 0 ? "text-red-700" : "text-muted-foreground"
                  )}
                >
                  {tx.amount > 0 ? "+" : ""}
                  {fmtKm(tx.amount)}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {tx.adminName
                    ? `Admin: ${tx.adminName}`
                    : tx.sessionScheduledAt
                      ? `Sesija: ${tx.therapistName ?? "?"} · ${fmtDateTime(tx.sessionScheduledAt)}`
                      : "—"}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{tx.note ?? "—"}</td>
                {showReceipt && (
                <td className="px-4 py-3 text-right">
                  {tx.reason === "recharge" && tx.invoiceId ? (
                    <a
                      href={`/api/company-invoices/${tx.invoiceId}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      title="Faktura za dopunu (PDF)"
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium tabular-nums text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <FilePdf className="size-3.5" />
                      {tx.invoiceNumber ?? "Faktura"}
                    </a>
                  ) : tx.reason === "recharge" && invoiceUrl ? (
                    <button
                      type="button"
                      disabled={loadingId === tx.id}
                      onClick={() => generateReceipt(tx.id)}
                      title="Stara dopuna, izdata prije faktura za dopunu. Potvrda nije fiskalni račun."
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                    >
                      {loadingId === tx.id ? (
                        <CircleNotch className="size-3.5 animate-spin" />
                      ) : (
                        <FileText className="size-3.5" />
                      )}
                      Potvrda
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > pageSize && (
        <div className="flex items-center justify-between border-t px-4 py-3 text-xs">
          <span className="text-muted-foreground">
            Strana {page + 1} od {lastPage}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => onPageChange(page - 1)}
              className="text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              ‹ Prethodna
            </button>
            <button
              disabled={page + 1 >= lastPage}
              onClick={() => onPageChange(page + 1)}
              className="text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              Sljedeća ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
